import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  LogReturnRequest,
  ReturnActionKind,
  ReturnActionRequest,
  ReturnActionResult,
  ReturnCheckRequest,
  ReturnDetail,
  ReturnMessageRequest,
  ReturnNoteRequest,
  ReturnsListParams,
  ReturnsListResponse,
  ReturnSyncResult,
  ReturnTrackingRequest,
} from "../models/returns/IReturnsApi";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";

export const RETURNS_PAGE_SIZE = 50;

export const returnsKeys = {
  all: ["returns"] as const,
  list: (params: ReturnsListParams) => ["returns", "list", params] as const,
  detail: (id?: string) => ["returns", "detail", id] as const,
};

export const returnPath = (id: string) => `/returns/${encodeURIComponent(id)}`;

/** The list's query string. Empty values are left out so the key (and the URL) stay short. */
export function returnsListQuery(params: ReturnsListParams): string {
  const query = new URLSearchParams();
  query.set("tab", params.tab);
  if (params.channel !== "all") query.set("channel", params.channel);
  if (params.search) query.set("search", params.search);
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  query.set("page", String(params.page));
  query.set("pageSize", String(params.pageSize));
  return `?${query.toString()}`;
}

async function unwrap<T>(
  request: Promise<DefaultResponseWithData<T>>,
): Promise<T> {
  const res = await request;
  if (!res.success) throw new Error(res.message);
  return res.data;
}

/**
 * The API answers 200 with data.blocked when live actions are switched off here (staging, local);
 * that isn't an error, nothing reached eBay. Any other refusal (eBay said no, bad request) is
 * thrown so the caller shows the API's message.
 */
async function outcome(
  request: Promise<DefaultResponseWithData<ReturnActionResult | null>>,
): Promise<ReturnActionResult> {
  const res = await request;
  if (res.data?.blocked)
    return {
      ...res.data,
      blocked: true,
      message: res.data.message || res.message,
    };
  if (!res.success)
    throw new Error(res.message || "Something went wrong – nothing was done.");
  return {
    caseId: null,
    refundStatus: null,
    detail: null,
    ...res.data,
    blocked: false,
    message: res.message || res.data?.message || "",
  };
}

/** One page of returns with the tab counts, tiles and the reasons bar. Server-paged, sorted by eBay's deadline. */
export function useReturnsList(
  params: ReturnsListParams,
  enabled = true,
  componentName = "useReturnsList",
) {
  const { get } = useApiHelper(apiConfig(componentName));

  const list = useQuery<ReturnsListResponse>(
    returnsKeys.list(params),
    () =>
      unwrap(
        get<DefaultResponseWithData<ReturnsListResponse>>(
          `/returns/list${returnsListQuery(params)}`,
        ),
      ),
    {
      enabled,
      staleTime: 30_000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  return { list };
}

/** One return. Polled every minute so eBay's state and deadlines follow the sync. */
export function useReturn(
  id: string | undefined,
  enabled = true,
  componentName = "useReturn",
) {
  const { get } = useApiHelper(apiConfig(componentName));

  const detail = useQuery<ReturnDetail>(
    returnsKeys.detail(id),
    () =>
      unwrap(
        get<DefaultResponseWithData<ReturnDetail>>(returnPath(id as string)),
      ),
    {
      enabled: enabled && !!id,
      staleTime: 30_000,
      refetchInterval: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );

  return { detail };
}

interface IActVariables {
  id: string;
  kind: ReturnActionKind;
  body: ReturnActionRequest;
}

interface IMessageVariables {
  id: string;
  body: string;
}

interface INoteVariables {
  id: string;
  body: string;
  mentions: string[];
}

interface IDeleteNoteVariables {
  id: string;
  eventId: string;
}

/** Every write to a return. Each one refetches every returns query once it has settled, blocked or not. */
export function useReturnsMutations(componentName = "useReturnsMutations") {
  const { post, del } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const refresh = () => queryClient.invalidateQueries(returnsKeys.all);
  const settled = { onSettled: () => refresh() };

  const sync = useMutation(
    () =>
      unwrap(
        post<undefined, DefaultResponseWithData<ReturnSyncResult>>(
          "/returns/sync",
          undefined,
        ),
      ),
    settled,
  );

  const act = useMutation(
    ({ id, kind, body }: IActVariables) =>
      outcome(
        post<
          ReturnActionRequest,
          DefaultResponseWithData<ReturnActionResult | null>
        >(`${returnPath(id)}/actions/${kind}`, body),
      ),
    settled,
  );

  const message = useMutation(
    ({ id, body }: IMessageVariables) =>
      outcome(
        post<
          ReturnMessageRequest,
          DefaultResponseWithData<ReturnActionResult | null>
        >(`${returnPath(id)}/messages`, { body }),
      ),
    settled,
  );

  const addNote = useMutation(
    ({ id, body, mentions }: INoteVariables) =>
      unwrap(
        post<ReturnNoteRequest, DefaultResponseWithData<ReturnDetail>>(
          `${returnPath(id)}/notes`,
          { body, mentions },
        ),
      ),
    settled,
  );

  const deleteNote = useMutation(
    ({ id, eventId }: IDeleteNoteVariables) =>
      unwrap(
        del<DefaultResponseWithData<ReturnDetail>>(
          `${returnPath(id)}/notes/${encodeURIComponent(eventId)}`,
        ),
      ),
    settled,
  );

  // Both answer with the updated return, which is put straight into the cache so the page
  // (and the refund or message panel opened next) works from the saved state.
  const keep = {
    onSuccess: (detail: ReturnDetail, { id }: { id: string }) =>
      queryClient.setQueryData(returnsKeys.detail(id), detail),
    ...settled,
  };

  // The check also answers with what eBay was told (or why not) in the message, for the line under it.
  const check = useMutation(
    async ({ id, body }: { id: string; body: ReturnCheckRequest }) => {
      const res = await post<
        ReturnCheckRequest,
        DefaultResponseWithData<ReturnDetail>
      >(`${returnPath(id)}/check`, body);
      if (!res.success) throw new Error(res.message);
      return { detail: res.data, message: res.message || "" };
    },
    {
      onSuccess: (
        { detail }: { detail: ReturnDetail; message: string },
        { id }: { id: string },
      ) => queryClient.setQueryData(returnsKeys.detail(id), detail),
      ...settled,
    },
  );

  const tracking = useMutation(
    ({ id, body }: { id: string; body: ReturnTrackingRequest }) =>
      unwrap(
        post<ReturnTrackingRequest, DefaultResponseWithData<ReturnDetail>>(
          `${returnPath(id)}/tracking`,
          body,
        ),
      ),
    keep,
  );

  const logReturn = useMutation(
    (body: LogReturnRequest) =>
      unwrap(
        post<LogReturnRequest, DefaultResponseWithData<ReturnDetail>>(
          "/returns/log",
          body,
        ),
      ),
    settled,
  );

  return {
    sync,
    act,
    message,
    addNote,
    deleteNote,
    check,
    tracking,
    logReturn,
    refresh,
  };
}

export default useReturnsMutations;
